import { useCallback, useState, CSSProperties } from "react";
import { IInstitution, updateInstitutionSteps } from "../../redux/reducer/institution";
import useIsMobile from "../../utils/useIsMobile";
import {
    Button,
    Col,
    Row,
    Space,
    Switch,
    Tag,
    Card,
    Typography,
    Alert,
    Badge,
    Tooltip,
    Empty,
    message,
} from "antd";
import {
    CheckOutlined,
    ReloadOutlined,
    DragOutlined,
    SaveOutlined,
    PoweroffOutlined,
    LockOutlined,
    UnlockOutlined,
    InfoCircleOutlined,
    OrderedListOutlined,
} from "@ant-design/icons";
import { ConfigStep, ConfigStepId, getSteps } from "../../utils/configuration";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { useMsal } from "@azure/msal-react";

const { Title, Paragraph } = Typography;

const styles: Record<string, CSSProperties> = {
    container: {
        padding: '24px',
    },
    header: {
        marginBottom: '24px',
    },
    stepCard: {
        height: '100%',
        transition: 'all 0.3s',
        cursor: 'move',
        borderRadius: '8px',
        overflow: 'hidden',
    },
    stepCardDragging: {
        opacity: 0.5,
        transform: 'scale(0.95)',
    },
    stepCardDragOver: {
        borderColor: '#1890ff',
        borderWidth: '2px',
        borderStyle: 'dashed',
    },
    stepCardDisabled: {
        opacity: 0.6,
        backgroundColor: '#fafafa',
    },
    dragHandle: {
        fontSize: '20px',
        color: '#999',
        cursor: 'grab',
        padding: '8px',
        transition: 'all 0.3s',
    },
    dragHandleActive: {
        cursor: 'grabbing',
        color: '#1890ff',
    },
    stepNumber: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: '#f0f0f0',
        fontWeight: 600,
        marginRight: '12px',
    },
    stepNumberEnabled: {
        backgroundColor: '#1890ff',
        color: '#fff',
    },
    actionSection: {
        backgroundColor: '#fafafa',
        padding: '16px 24px',
        borderRadius: '8px',
        marginBottom: '24px',
    },
    infoAlert: {
        marginBottom: '24px',
    },
};

interface StepsConfigurationParams {
    institution: IInstitution;
}

const StepsConfiguration = ({
    institution,
}: StepsConfigurationParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const { accounts } = useMsal();
    const isMobile = useIsMobile();

    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['institution/PATCH_INSTITUTION_STEPS']);

    const [currentConfig, setCurrentConfig] = useState<ConfigStep[]>(getSteps(institution.services));

    const [draggedStep, setDraggedStep] = useState<string | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
    const [hasChanges, setHasChanges] = useState(false);

    const handleDragStart = (e: React.DragEvent, stepId: string) => {
        setDraggedStep(stepId);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverIndex(index);
    };

    const handleDragLeave = () => {
        setDragOverIndex(null);
    };

    const handleDrop = (e: React.DragEvent, dropIndex: number) => {
        e.preventDefault();

        if (!draggedStep) return;

        const draggedIndex = currentConfig.findIndex(step => step.id === draggedStep);
        if (draggedIndex === -1) return;

        const newSteps = [...currentConfig];
        const [draggedItem] = newSteps.splice(draggedIndex, 1);
        newSteps.splice(dropIndex, 0, draggedItem);

        setCurrentConfig(newSteps);
        setHasChanges(true);
        setDraggedStep(null);
        setDragOverIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedStep(null);
        setDragOverIndex(null);
    };

    const handleSaveConfiguration = useCallback(() => {
        dispatch(updateInstitutionSteps({
            input: {
                services: currentConfig,
                id: institution.id,
                institutionId: institution.institutionId,
            },
            message: { success: 'Successfully saved configuration' },
            headers: {
                Authorization: `Bearer ${accounts[0].idToken}`,
            },
            onSuccess: () => {
                setHasChanges(false);
            },
        }));
    }, [currentConfig, institution, accounts, dispatch]);


    const handleResetConfiguration = () => {
        setCurrentConfig(institution.services);
        setHasChanges(false);
        message.info('Configuration reset to saved state');
    };

    const getStepTagColor = (step: ConfigStep) => {
        if (!step.enabled && step.conditionalEnabling) return 'blue';
        if (!step.enabled) return 'default';
        if (step.required) return 'red';
        return 'green';
    };

    const getStepTagText = (step: ConfigStep) => {
        if (!step.enabled && step.conditionalEnabling) return 'Conditional';
        if (!step.enabled) return 'Disabled';
        if (step.required) return 'Required';
        return 'Optional';
    };

    const handleToggleStep = (stepId: string) => {
        setCurrentConfig(
            currentConfig.map(step =>
                step.id === stepId
                    ? { ...step, enabled: !step.enabled, ...(!step.enabled ? { conditionalEnabling: false } : {}) }
                    : step
            )
        );
        setHasChanges(true);
    };

    const handleToggleConditionalEnabling = (stepId: string) => {
        setCurrentConfig(
            currentConfig.map(step =>
                step.id === stepId
                    ? { ...step, conditionalEnabling: !(step.conditionalEnabling ?? false) }
                    : step
            )
        );
        setHasChanges(true);
    };

    const enabledStepsCount = currentConfig.filter(step => step.enabled).length;

    return (
        <div style={{
            ...styles.container,
            padding: isMobile ? '12px 0' : '24px'
        }}>
            {/* Header with actions */}
            <div style={styles.actionSection}>
                <Row justify="space-between" align="middle">
                    <Col>
                        <Space size="large">
                            <Title level={4} style={{ margin: 0 }}>
                                <OrderedListOutlined /> Workflow Steps
                            </Title>
                            <Badge
                                count={`${enabledStepsCount} Active`}
                                style={{ backgroundColor: '#52c41a' }}
                            />
                            {hasChanges && (
                                <Tag color="orange">Unsaved Changes</Tag>
                            )}
                        </Space>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={handleResetConfiguration}
                                disabled={!hasChanges}
                            >
                                Reset
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSaveConfiguration}
                                loading={loading}
                                disabled={!hasChanges}
                            >
                                Save Configuration
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* Info Alert */}
            <Alert
                style={styles.infoAlert}
                message="Drag and Drop to Reorder Steps"
                description="Use the drag handle to reorder steps. The order determines the sequence users will follow."
                type="info"
                showIcon
                closable
                icon={<InfoCircleOutlined />}
            />

            {/* Steps Grid */}
            {currentConfig.length === 0 ? (
                <Empty description="No steps configured" />
            ) : (
                <Row gutter={[16, 16]}>
                    {currentConfig.map((step, index) => {
                        const isDragging = draggedStep === step.id;
                        const isDragOver = dragOverIndex === index;

                        return (
                            <Col xs={24} sm={12} lg={8} key={step.id}>
                                <Card
                                    draggable={!step.required}
                                    onDragStart={(e) => handleDragStart(e, step.id)}
                                    onDragOver={(e) => handleDragOver(e, index)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, index)}
                                    onDragEnd={handleDragEnd}
                                    style={{
                                        ...styles.stepCard,
                                        ...(isDragging ? styles.stepCardDragging : {}),
                                        ...(isDragOver ? styles.stepCardDragOver : {}),
                                        ...(!step.enabled ? styles.stepCardDisabled : {}),
                                    }}
                                    styles={{ body: { padding: '16px' } }}
                                    hoverable={step.enabled}
                                >
                                    <Row align="top" gutter={12}>
                                        <Col flex="none">
                                            <Tooltip title={step.required ? "Required step (cannot reorder)" : "Drag to reorder"}>
                                                <DragOutlined
                                                    style={{
                                                        ...styles.dragHandle,
                                                        ...(isDragging ? styles.dragHandleActive : {}),
                                                        ...(step.required ? { cursor: 'not-allowed', opacity: 0.4 } : {}),
                                                    }}
                                                />
                                            </Tooltip>
                                        </Col>
                                        <Col flex="auto">
                                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                                <Row justify="space-between" align="middle">
                                                    <Col>
                                                        <Space align="center">
                                                            <span style={{
                                                                ...styles.stepNumber,
                                                                ...(step.enabled ? styles.stepNumberEnabled : {}),
                                                            }}>
                                                                {index + 1}
                                                            </span>
                                                            <Title level={5} style={{ margin: 0, display: 'inline' }}>
                                                                {step.name}
                                                            </Title>
                                                        </Space>
                                                    </Col>
                                                    <Col>
                                                        <Space>
                                                            <Tooltip title="Toggle step on/off">
                                                                <Switch
                                                                    checked={step.enabled}
                                                                    onChange={() => handleToggleStep(step.id)}
                                                                    checkedChildren={<CheckOutlined />}
                                                                    unCheckedChildren={<PoweroffOutlined />}
                                                                />
                                                            </Tooltip>
                                                        </Space>
                                                    </Col>
                                                </Row>

                                                <>
                                                    <Paragraph
                                                        type={step.enabled ? undefined : 'secondary'}
                                                        style={{ marginBottom: '12px', fontSize: '14px' }}
                                                    >
                                                        {step.description}
                                                    </Paragraph>

                                                    <Space size="small">
                                                        <Tag
                                                            color={getStepTagColor(step)}
                                                            icon={step.required ? <LockOutlined /> : <UnlockOutlined />}
                                                        >
                                                            {getStepTagText(step)}
                                                        </Tag>
                                                        {!step.enabled && !step.conditionalEnabling && (
                                                            <Tag>Inactive</Tag>
                                                        )}
                                                    </Space>
                                                    {!step.enabled && (step.id === ConfigStepId.business || step.id === ConfigStepId.transcript) && (
                                                        <Space size="small" style={{ marginTop: '8px' }}>
                                                            <Tooltip title="When enabled, this step will appear only for applicants who have relevant data">
                                                                <Switch
                                                                    size="small"
                                                                    checked={step.conditionalEnabling ?? false}
                                                                    onChange={() => handleToggleConditionalEnabling(step.id)}
                                                                />
                                                            </Tooltip>
                                                            <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                                                                Conditional
                                                            </Typography.Text>
                                                        </Space>
                                                    )}
                                                </>
                                            </Space>
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}
        </div>
    );
};

export default StepsConfiguration;
